import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { CacheService } from 'ionic-cache';
import { API_URL, PORT, PREFIX } from '../../environments/environment';
import {BehaviorSubject, EMPTY, from, throwError} from 'rxjs';
import { last, mergeMap, share } from 'rxjs/operators';
//import { isArray } from '/util/isArray';
import { AuthenticationService } from './authentication.service';
import { CentralAuthenticationService } from './central-authentication.service';
import { FeedbackService } from './feedback.service';
import { saveAs } from 'file-saver-es';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(
    private centralAuth: CentralAuthenticationService,
    private auth: AuthenticationService,
    private http: HttpClient,
    private cache: CacheService,
    private feedbackService: FeedbackService,
  ) { }

  /**
   * Parametrized and cached Get Request
   *
   * @param $arguments
   * @param central
   */
  public cachedGetRequest($arguments: GetRequestArguments, central: boolean = false) {
    const args = $arguments.get();

    const params = args.params;
    if (central) {
      const response = from(this.centralAuth.getToken()).pipe(mergeMap(token => {
        if (token) {
          const headers = new HttpHeaders({
            authorization: token.token_type + ' ' + token.access_token,
          });
          const request = this.http.get(API_URL + String(args.url), { headers, params });
          if (args.cacheGroup !== undefined) {
            return args.delayed ?
              this.cache.loadFromDelayedObservable(
                'central-' + args.url + args.cacheOffset + '-' + token.email, request,
                args.cacheGroup,
                args.ttl,
                args.refresh).pipe(share())
              : this.cache.loadFromObservable(
                'central-' + args.url + args.cacheOffset + '-' + token.email,
                request,
                args.cacheGroup,
                args.ttl).pipe(share());
          } else {
            return request.pipe(share());
          }
        } else {
          this.centralAuth.removeToken();
          return EMPTY;
        }

      })).pipe(share());
      return response;
    } else {
      const response = from(this.auth.getTenantToken()).pipe(mergeMap(token => {
        if (token) {
          const headers = new HttpHeaders({
            authorization: token.token_type + ' ' + token.access_token,
          });
          const request = this.http.get(PREFIX + token.domains[0].domain + PORT + '/api/' + String(args.url), { headers, params });
          if (args.cacheGroup !== undefined) {
            return args.delayed ?
              this.cache.loadFromDelayedObservable(
                token.domains[0].domain + '-' + args.url + args.cacheOffset + '-' + token.email,
                request,
                args.cacheGroup,
                args.ttl,
                args.refresh).pipe(share(), last(undefined, null)) // the last() means that after the observable emits all its values, only the last one will be emitted, effectively making the observable emit only the final value at the end
              : this.cache.loadFromObservable(
                token.domains[0].domain + '-' + args.url + args.cacheOffset + '-' + token.email,
                request,
                args.cacheGroup,
                args.ttl).pipe(share());
          } else {
            return request.pipe(share());
          }
        } else {
          this.auth.removeToken();
          return EMPTY;
        }

      })).pipe(share());
      return response;
    }


  }

  public deleteRequest($arguments: PostRequestArguments, central: boolean = false) {
    const args = $arguments.get();

    const params = args.params;

    if (central) {
      const response = from(this.centralAuth.getToken()).pipe(mergeMap(token => {
        if (token) {
          const headers = new HttpHeaders({
            authorization: token.token_type + ' ' + token.access_token,
          });
          let id;
          if (args.object.id) {
            id = args.object.id;
          } else if (!isNaN(Number(args.object))) {
            id = Number(args.object);
          }
          return this.http.delete(API_URL + String(args.url) + '/' + id, { headers, params });

        } else {
          this.centralAuth.removeToken();
          throw throwError('Could not find usable token, request aborted');
        }
      })).pipe(share());
      response.pipe(share()).subscribe(
        () => {},
        error => { this.feedbackService.standardError(error, 3);  }
      );
      return response;
    } else {
      const response = from(this.auth.getTenantToken()).pipe(mergeMap(token => {
        if (token) {
          const headers = new HttpHeaders({
            authorization: token.token_type + ' ' + token.access_token,
          });
          let id;
          if (args.object.id) {
            id = args.object.id;
          } else if (!isNaN(Number(args.object))) {
            id = Number(args.object);
          }
          return this.http.delete(PREFIX + token.domains[0].domain + PORT + '/api/' + String(args.url) + '/' + id, { headers, params });

        } else {
          this.auth.removeToken();
          throw throwError('Could not find usable token, request aborted');
        }
      })).pipe(share());
      response.pipe(share()).subscribe(
        () => {},
        error => { if (error.message) { this.feedbackService.standardError(error, 3); } }
      );
      return response;
    }

  }

  public postRequest($arguments: PostRequestArguments, central: boolean = false) {
    const args = $arguments.get();

    const params = args.params;

    if (central) {
      const response = from(this.centralAuth.getToken()).pipe(mergeMap(token => {
        if (token) {
          const headers = new HttpHeaders({
            authorization: token.token_type + ' ' + token.access_token,
          });
          return this.http.post(API_URL + String(args.url),
           args.object,
           { headers, params, responseType: (args.responseType ?? 'json') as 'json'}
          );
        } else {
          this.centralAuth.removeToken();
          throw throwError('Could not find usable token, request aborted');
        }
      })).pipe(share());
      response.pipe(share()).subscribe(
        () => {},
        error => { this.feedbackService.standardError(error, 3);  }
      );
      return response;
    } else {
      const response = from(this.auth.getTenantToken()).pipe(mergeMap(token => {
        if (token) {
          const headers = new HttpHeaders({
            authorization: token.token_type + ' ' + token.access_token,
          });
          return this.http.post(PREFIX + token.domains[0].domain + PORT + '/api/' + String(args.url),
            args.object,
            { headers, params, responseType: (args.responseType ?? 'json') as 'json'}
        );
        } else {
          this.auth.removeToken();
          throw throwError('Could not find usable token, request aborted');
        }
      })).pipe(share());
      response.pipe(share()).subscribe(
        () => {},
        error => { if (error.message) { this.feedbackService.standardError(error, 3); } }
      );
      return response;
    }
  }

  public clearCacheGroup(group) {
    return this.cache.clearGroup(group);
  }

  public createFormData(object) {
    const formData = new FormData();
    for (const key of Object.keys(object)) {
      let value = object[key];
      if (value !== undefined && value !== null) {
        if (value instanceof Object) {
          value = JSON.stringify(value);
        }
        formData.append(key, value);
      }
    }
    return formData;
  }

  public getCacheUrl = (url: string | Array<string | number>, central: boolean = false, cacheOffset: string = '') => {
    url = buildUrl(url);
    if (central) {
      return this.centralAuth.getToken().then(token => 'central-' + url + cacheOffset + '-' + token.email);
    } else {
      return this.auth.getTenantToken().then(token => token.domains[0].domain + '-' + url + cacheOffset + '-' + token.email);
    }
  };

  public updateObservable(data, subject: BehaviorSubject<any>) {
    if (!_.isEqual(data, subject.value)) {
      subject.next(data);
    }
  }

  public downloadFile(data: any, type: string, filename: string) {
    const blob = new Blob([data], { type: type});
    const url= window.URL.createObjectURL(blob);
    saveAs(blob,filename);
  }
}

export type Refresh = 'expired' | 'all';

export type All = '' | '/all';

interface IGetRequestArguments {
  url: string | Array<string | number>;
  cacheGroup: string | undefined;
  cacheOffset?: string;
  ttl?: number;
  refresh?: Refresh;
  params?: HttpParams;
  delayed?: boolean;
  getAll?: All | boolean;
}
const getRequestDefaults: IGetRequestArguments = {
  url: null,
  cacheGroup: undefined,
  cacheOffset: '',
  ttl: 60 * 60 * 4,
  refresh: 'expired',
  params: new HttpParams(),
  delayed: true,
  getAll: false,
};

export const buildUrl = (url) => {
  if (Array.isArray(url)) {
    let newUrl = url.shift();
    for (const comp of url) {
      newUrl += '/' + String(comp);
    }
    return newUrl;
  } else {
    return url;
  }
};

export class GetRequestArguments {
  private readonly arguments: IGetRequestArguments;

  constructor(args: IGetRequestArguments) {
    this.arguments = { ...getRequestDefaults, ...args};
    this.arguments.getAll = this.arguments.getAll ? '/all' : '';
    this.arguments.url = buildUrl(this.arguments.url) + this.arguments.getAll;
  }

  public get(): IGetRequestArguments {
    return this.arguments;
  }

}

interface IPostRequestArguments {
  url: string | Array<string | number>;
  object: any;
  params?: HttpParams;
  responseType?: 'json' | 'text' | 'blob';
}
const postRequestDefaults: IPostRequestArguments = {
  url: null,
  object: null,
  params: new HttpParams(),
  responseType: 'json'
};

export class PostRequestArguments {
  private readonly arguments: IPostRequestArguments;

  constructor(args: IPostRequestArguments) {
    this.arguments = { ...postRequestDefaults, ...args};
    this.arguments.url = buildUrl(this.arguments.url);
  }

  public get(): IPostRequestArguments {
    return this.arguments;
  }
}
